import styled from "styled-components";

import PhoneMockup from "./PhoneMockup";

import { Tab } from "@customType/Tab";
import { cls } from "src/theme/colors";
import { Capture, MYD_CAPTURES, MYSTOCK_CAPTURES } from "src/assets/images/captures";

interface Service {
  tab: Tab;
  name: string;
  subText: string;
  description: string[];
  capture: Capture;
  color: string;
  background: string;
}

const SERVICES: Service[] = [
  {
    tab: "myd",
    name: "마이디",
    subText: "가장 안전한 데이터 거래를 위해",
    description: ["마이데이터 거래 기반", "데이터테크 플랫폼"],
    capture: MYD_CAPTURES[0],
    color: cls.mainBlue5,
    background: cls.blue0,
  },
  {
    tab: "mystock",
    name: "마이주식",
    subText: "같은 종목을 보는 사람들과 함께",
    description: ["내 매매 기록에서 시작되는", "주식 커뮤니티"],
    capture: MYSTOCK_CAPTURES[0],
    color: cls.stockBlue,
    background: "#f1f2fe",
  },
];

// myd.world 홈. 서비스별 화면은 헤더 탭과 같은 해시 링크(#myd, #mystock)로 이동한다.
function Home() {
  return (
    <S.Container>
      <S.SubText>내 데이터도, 내 매매 기록도</S.SubText>
      <S.MainText>
        <S.Highlight>myd.world</S.Highlight>에서 만나요
      </S.MainText>
      <S.Cards>
        {SERVICES.map(({ tab, name, subText, description, capture, color, background }) => (
          <S.Card key={tab} href={`#${tab}`} $background={background}>
            <S.CardText>
              <S.CardSubText>{subText}</S.CardSubText>
              <S.CardName $color={color}>{name}</S.CardName>
              <S.CardDescription>
                {description[0]}
                <br />
                {description[1]}
              </S.CardDescription>
              <S.CardButton $color={color}>
                {name} 보러가기 <span aria-hidden="true">&gt;</span>
              </S.CardButton>
            </S.CardText>
            {/* 카드 전체가 링크라서, 꾸밈용인 화면 캡처까지 링크 이름으로 읽히지 않게 대체 텍스트를 비운다. */}
            <PhoneMockup src={capture.src} alt="" width={310} />
          </S.Card>
        ))}
      </S.Cards>
    </S.Container>
  );
}

export default Home;

const S = {
  Container: styled.section`
    width: 100%;
    padding: 110px 240px 140px;
    background-color: ${cls.white};
  `,
  SubText: styled.p`
    text-align: center;
    font-size: 40px;
    letter-spacing: -0.92px;
    margin-bottom: 10px;
    white-space: nowrap;
  `,
  MainText: styled.h1`
    text-align: center;
    font-size: 60px;
    font-weight: 700;
    line-height: 80px;
    letter-spacing: -1.38px;
    white-space: nowrap;
  `,
  Highlight: styled.span`
    color: ${cls.mainBlue5};
  `,
  Cards: styled.div`
    display: flex;
    gap: 40px;
    margin-top: 80px;
  `,
  Card: styled.a<{ $background: string }>`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    height: 760px;
    /* Noto Sans KR 이 없는 PC 에서는 글자 폭이 더 넓은 기본 글꼴로 보이므로, 글과 휴대폰 사이에 여유를 둔다. */
    padding: 0 60px 0 70px;
    border-radius: 20px;
    color: ${cls.mainBlack};
    background: ${({ $background }) => $background};
    transition: transform 0.3s, box-shadow 0.3s;
    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
    }
    /* 카드가 떠오르며 비운 아래쪽을 투명한 영역으로 덮어, 포인터가 아래 가장자리에 있을 때 카드가 떨리지 않게 한다. */
    &:hover::after {
      position: absolute;
      top: 100%;
      right: 0;
      left: 0;
      height: 8px;
      content: "";
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
      &:hover {
        transform: none;
      }
    }
  `,
  CardText: styled.div`
    display: flex;
    flex-direction: column;
  `,
  CardSubText: styled.p`
    font-size: 26px;
    letter-spacing: -0.6px;
    white-space: nowrap;
  `,
  CardName: styled.h2<{ $color: string }>`
    margin-top: 12px;
    color: ${({ $color }) => $color};
    font-size: 60px;
    font-weight: 700;
    line-height: 80px;
    letter-spacing: -1.38px;
    white-space: nowrap;
  `,
  CardDescription: styled.p`
    margin-top: 16px;
    font-size: 28px;
    line-height: 42px;
    letter-spacing: -0.64px;
    white-space: nowrap;
  `,
  CardButton: styled.span<{ $color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    height: 72px;
    padding: 0 36px;
    margin-top: 56px;
    border-radius: 36px;
    color: ${cls.white};
    background: ${({ $color }) => $color};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.2px;
    white-space: nowrap;
    /* 화살표를 따로 감싸면 flex 안에서 앞 공백이 사라지므로 간격을 직접 준다. */
    span {
      margin-left: 6px;
    }
  `,
};
